// Phase 6 — Secure Learner Video Player.
// Guards:
//   • Fast-forward blocked (revert to server-tracked max on any forward seek)
//   • Unique-watch tracking (skipped time never counted)
//   • Progress reported every 8s + on pause/end with max_watched_position + unique_watched_seconds
//   • Steward-logo watermark (top-right) + dynamic learner watermark (bottom-left) with live timestamp
//   • Keyboard forward-seek shortcuts (→, L, J, End) blocked
//   • Right-click / download / picture-in-picture disabled
//   • Playback-rate locked unless backend security.allow_playback_speed = true
import { useCallback, useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { toast } from "sonner";
import { Loader2, ShieldAlert, CheckCircle2 } from "lucide-react";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { YouTubeSecurePlayer } from "@/components/learner/YouTubeSecurePlayer";

function embedYoutube(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return `https://www.youtube.com/embed${u.pathname}`;
    const id = u.searchParams.get("v");
    if (id) return `https://www.youtube.com/embed/${id}`;
  } catch { /* ignore */ }
  return url;
}
function embedVimeo(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes("vimeo.com")) {
      const id = u.pathname.replace(/\//g, "");
      return `https://player.vimeo.com/video/${id}`;
    }
  } catch { /* ignore */ }
  return url;
}

const fmtTime = (s) => {
  if (!Number.isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60), r = Math.floor(s % 60);
  return `${m}:${String(r).padStart(2, "0")}`;
};

export function LessonVideoPlayer({ lessonId, onCompleted, autoPlay = false, onPlayStarted }) {
  const { user } = useAuth();
  const videoRef = useRef(null);
  const containerRef = useRef(null);   // fullscreen wraps this so the overlays stay visible
  const [payload, setPayload] = useState(null);
  const [err, setErr] = useState(null);
  // Mutable playback trackers (refs so useEffect doesn't churn)
  const maxWatchedRef = useRef(0);
  const uniqueRef = useRef(0);
  const lastTickRef = useRef(0);
  // Resume machinery -----------------------------------------------------
  // `resumeTargetRef` is the seconds value we must jump to on first load
  // for THIS lesson. It is set once from the metadata payload and cleared
  // only after we have confirmed the video is playing at (or past) that
  // point. Until then, EVERY seeking event and EVERY server-side "fast
  // forward" response is treated as a benign side-effect of the resume —
  // never toasted, never snapped-back-to-zero.
  const resumeTargetRef = useRef(0);
  const resumeAppliedRef = useRef(false);
  // Anti-skip machinery ----
  // `programmaticSeekRef` is true while WE (or the browser during HLS gap
  // handling) drive the currentTime — never treat that as a user fast-forward.
  // Cleared on a 2500ms timer so a burst of internal seek events all count as
  // the same programmatic action (HLS segment fetches on cold pods can take
  // over a second on first play).
  const programmaticSeekRef = useRef(false);
  const programmaticSeekTimerRef = useRef(null);
  // Last position we know the learner legitimately watched — used as the
  // rewind target when we need to reject an intentional forward seek.
  const lastValidPositionRef = useRef(0);
  // One-shot toast throttle so a stuck seek doesn't spam the UI.
  const lastWarnAtRef = useRef(0);
  const completedRef = useRef(false);
  // Snapshot of "was this lesson already completed the moment we
  // mounted?". If YES, the heartbeat will report is_completed=true on
  // the very first tick — we must NOT trigger the auto-advance
  // countdown then (the learner explicitly re-opened this lesson to
  // re-watch it). STATE_ENDED / <video> `ended` are still allowed to
  // fire auto-advance because those events represent the learner
  // actually finishing the re-watch.
  const wasCompletedAtMountRef = useRef(false);
  const onCompletedFiredRef = useRef(false);
  const fireCompletedOnce = () => {
    if (onCompletedFiredRef.current) return;
    onCompletedFiredRef.current = true;
    if (typeof onCompleted === "function") onCompleted();
  };
  const [tsClock, setTsClock] = useState(() => new Date());
  const [watchedPct, setWatchedPct] = useState(0);
  // Ref mirror of watchedPct so reportProgress can read the latest value
  // without re-creating the callback on every timeupdate tick.
  const watchedPctRef = useRef(0);

  // Helper: mark the next currentTime write as programmatic and auto-clear.
  const startProgrammaticSeek = () => {
    programmaticSeekRef.current = true;
    if (programmaticSeekTimerRef.current) clearTimeout(programmaticSeekTimerRef.current);
    programmaticSeekTimerRef.current = setTimeout(() => {
      programmaticSeekRef.current = false;
    }, 2500);
  };

  // Load metadata + security
  useEffect(() => {
    let stopped = false;
    setPayload(null); setErr(null);
    maxWatchedRef.current = 0; uniqueRef.current = 0;
    lastTickRef.current = 0; completedRef.current = false;
    lastValidPositionRef.current = 0;
    resumeTargetRef.current = 0;
    resumeAppliedRef.current = false;
    setCanMarkComplete(false);
    (async () => {
      try {
        const { data } = await api.get(`/learner/lessons/${lessonId}/video`);
        if (stopped) return;
        setPayload(data.data);
        // Prefer the server's tracked max (furthest watched), fall back to
        // last_watched / resume_from. All three are seconds.
        const resumed = Number(
          data.data?.max_watched_position
          ?? data.data?.last_watched_position_seconds
          ?? data.data?.resume_from_seconds
          ?? 0,
        );
        const isCompleted = !!data.data?.is_completed;
        maxWatchedRef.current = resumed;
        lastValidPositionRef.current = resumed;
        // Re-watch UX: if the lesson is already completed, don't seek
        // to the last-watched position (which for a 100 % completed
        // video is essentially the final frame — user complained they
        // couldn't re-watch it). Start from 0 instead so the learner
        // sees the full video again. Anti-skip is still safe because
        // maxWatchedRef stays at the full duration → seeking anywhere
        // inside the video is allowed for the re-watch.
        resumeTargetRef.current = isCompleted ? 0 : resumed;
        uniqueRef.current = Number(data.data?.unique_watched_seconds || 0);
        completedRef.current = isCompleted;
        wasCompletedAtMountRef.current = isCompleted;
        // Skip the "Resuming from…" toast for completed re-watches and
        // any lesson with no meaningful progress. Only show it for
        // genuinely in-progress lessons.
        if (!isCompleted && resumed > 3) {
          const mm = Math.floor(resumed / 60);
          const ss = Math.floor(resumed % 60).toString().padStart(2, "0");
          toast.info(`Resuming from where you left off (${mm}:${ss}).`);
        } else {
          // No meaningful resume needed — treat resume as already applied
          // so downstream guards don't wait forever.
          resumeAppliedRef.current = true;
        }
      } catch (e) { if (!stopped) setErr(formatApiError(e)); }
    })();
    return () => { stopped = true; };
  }, [lessonId]);

  // Live clock for the watermark
  useEffect(() => {
    const t = setInterval(() => setTsClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const [canMarkComplete, setCanMarkComplete] = useState(false);
  const [markingComplete, setMarkingComplete] = useState(false);
  // Tracks the lesson currently rendered so async reportProgress responses
  // that arrive AFTER the learner navigated away don't fire a stale
  // "completed" toast on the new lesson.
  const currentLessonIdRef = useRef(lessonId);
  useEffect(() => { currentLessonIdRef.current = lessonId; }, [lessonId]);

  const reportProgress = useCallback(async (currentTime, opts = {}) => {
    // Snapshot the lesson id at call time — the closure survives lesson
    // switches, so we need to know which lesson this heartbeat belongs to.
    const forLesson = lessonId;
    try {
      const { data } = await api.post(`/learner/lessons/${forLesson}/video-progress`, {
        current_time_seconds: Number(currentTime.toFixed(2)),
        watched_percentage: watchedPctRef.current,
        max_watched_position: Number(maxWatchedRef.current.toFixed(2)),
        unique_watched_seconds: Number(uniqueRef.current.toFixed(2)),
        seek_attempt: !!opts.seekAttempt,
      });
      // Silently drop the response if the learner has moved on. The server
      // still saved the progress — we just skip any UI side effects that
      // would otherwise appear on the wrong lesson.
      if (currentLessonIdRef.current !== forLesson) return;
      if (data?.success === false && data?.message?.toLowerCase().includes("fast forward")) {
        const allowed = Number(data.data?.allowed_position || 0);
        const v = videoRef.current;
        if (v) { startProgrammaticSeek(); v.currentTime = allowed; }
        // Silently correct during the resume phase — this is not a user
        // fast-forward, it's just the first heartbeat racing the resume
        // seek. Only surface the warning once the resume has been applied.
        if (resumeAppliedRef.current) {
          toast.warning("Fast forward is disabled. Please watch the video completely.");
        }
      }
      // Toggle the manual "Mark as complete" button when the learner has
      // watched the manual-gate threshold (default 95 %).
      if (data?.data?.can_mark_complete === true && !completedRef.current) {
        setCanMarkComplete(true);
      }
      if (data?.data?.is_completed && !completedRef.current) {
        completedRef.current = true;
        setCanMarkComplete(false);
        toast.success("Video lesson completed automatically.");
      }
      // Auto-advance fires from the heartbeat ONLY when this is a fresh
      // completion (was NOT already completed at mount). Otherwise the
      // very first tick on a re-watch would immediately jump the
      // learner to the next lesson before they finish re-watching.
      if (data?.data?.is_completed && !wasCompletedAtMountRef.current) {
        fireCompletedOnce();
      }
    } catch { /* silent — heartbeat should never blow up */ }
    // NOTE: reads watchedPctRef so `watchedPct` state churn (~4×/sec during
    // playback) never invalidates this callback. Kept in the deps list only
    // the two values that are TRULY stable per lesson.
  }, [lessonId, onCompleted]);

  const handleMarkComplete = useCallback(async () => {
    setMarkingComplete(true);
    try {
      const { data } = await api.post(`/learner/lessons/${lessonId}/mark-video-complete`);
      if (data?.data?.is_completed) {
        completedRef.current = true;
        setCanMarkComplete(false);
        toast.success("Marked as complete.");
        if (typeof onCompleted === "function") onCompleted();
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setMarkingComplete(false); }
  }, [lessonId, onCompleted]);

  // Attach HLS/MP4 + wire up anti-skip event listeners
  useEffect(() => {
    if (!payload || payload.source_type === "external_url") return undefined;
    const v = videoRef.current;
    if (!v) return undefined;
    const BASE = API_BASE.replace(/\/api$/, "");
    let hls = null;
    const hlsUrl = payload.hls_url ? `${BASE}${payload.hls_url}` : null;
    const streamUrl = `${BASE}${payload.playback_url}`;
    if (hlsUrl && Hls.isSupported()) {
      hls = new Hls({
        // Start on the lowest bitrate rendition so the player begins playing
        // in ~1s even on slow networks. HLS.js will auto-upgrade as bandwidth
        // is measured. This kills the "spins forever" symptom on first play.
        startLevel: 0,
        capLevelToPlayerSize: true,   // never fetch a rendition larger than viewport
        // Tell HLS.js to buffer & begin playback from the resume position
        // directly. This is the CORRECT way to resume an HLS stream —
        // setting v.currentTime after `loadedmetadata` fires can race with
        // HLS.js's own initial segment fetch and get overwritten to 0.
        // -1 means "use default (0)"; any non-negative value is honored.
        startPosition: resumeTargetRef.current > 3 ? resumeTargetRef.current : -1,
        // Conservative forward-buffer target — big enough for smooth playback,
        // small enough not to hammer storage during initial load.
        maxBufferLength: 30,          // seconds of forward buffer
        maxMaxBufferLength: 60,       // absolute upper cap
        maxBufferSize: 30 * 1024 * 1024,  // 30 MB
        backBufferLength: 30,         // keep 30s behind so rewind is instant
        lowLatencyMode: false,
        enableWorker: true,
        // Aggressive fragment retries: our storage-proxy caches on first hit
        // but the very first fetch can be slow on cold pods.
        fragLoadingMaxRetry: 6,
        fragLoadingRetryDelay: 500,
        manifestLoadingMaxRetry: 4,
        manifestLoadingRetryDelay: 500,
        levelLoadingMaxRetry: 4,
      });
      hls.loadSource(hlsUrl);
      hls.attachMedia(v);
      // Error recovery — networks flap, storage stalls: try to keep playing.
      // Bounded so an unrecoverable state (e.g. missing codec) doesn't cause
      // an infinite segment-fetch loop against the backend.
      let mediaRecoverTries = 0;
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (!data?.fatal) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          try { hls.startLoad(); } catch { /* ignore */ }
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          if (mediaRecoverTries >= 3) {
            try { hls.destroy(); } catch { /* ignore */ }
            toast.error("This video can't be played on this browser. Please try a different browser or device.");
            return;
          }
          mediaRecoverTries += 1;
          try { hls.recoverMediaError(); } catch { /* ignore */ }
        } else {
          try { hls.destroy(); } catch { /* ignore */ }
          toast.error("Video playback failed. Please refresh and try again.");
        }
      });
    } else if (hlsUrl && v.canPlayType("application/vnd.apple.mpegurl")) {
      v.src = hlsUrl;
    } else {
      v.src = streamUrl;
    }
    const security = payload.security || {};
    const preventFF = security.prevent_fast_forward !== false;
    const speedAllowed = !!security.allow_playback_speed;
    const allowedSpeeds = Array.isArray(security.allowed_playback_speeds) && security.allowed_playback_speeds.length
      ? security.allowed_playback_speeds
      : [1.0];

    // Resume from the server-tracked max position.
    // NOTE: we DO NOT guard on `v.duration` here — for HLS.js sources
    // `loadedmetadata` can fire before duration is populated, and
    // `Math.min(resume, 0)` would silently drop the resume back to 0.
    // HLS.js's `startPosition` config above already begins buffering at
    // the resume point; this handler covers plain-MP4 and native-HLS
    // (iOS Safari) code paths.
    const applyResume = () => {
      const target = resumeTargetRef.current || 0;
      if (target <= 3) {
        resumeAppliedRef.current = true;
        return;
      }
      // If we're already at or past the resume target (HLS.js honored
      // startPosition), just mark it applied.
      if (v.currentTime >= target - 1) {
        resumeAppliedRef.current = true;
        return;
      }
      startProgrammaticSeek();
      try { v.currentTime = target; } catch { /* ignore */ }
    };
    const onLoaded = () => { applyResume(); };
    v.addEventListener("loadedmetadata", onLoaded, { once: true });
    // Backup: some browsers/HLS.js builds reset currentTime to 0 during
    // the very first segment fetch. `canplay` fires once enough data is
    // buffered to actually play — verify we ended up at (or past) the
    // resume target and re-seek if not.
    const onCanPlay = () => {
      const target = resumeTargetRef.current || 0;
      if (target > 3 && v.currentTime < target - 2) {
        startProgrammaticSeek();
        try { v.currentTime = target; } catch { /* ignore */ }
      }
      resumeAppliedRef.current = true;
      // Auto-play if the parent asked (e.g. we just auto-advanced from
      // the previous lesson). Browsers block non-muted autoplay without
      // a user gesture — but the learner has been actively watching a
      // video, so `play()` from that user activation is generally
      // allowed. Fall back to muted playback if the browser still says
      // no. `onPlayStarted` clears the parent flag so the next fresh
      // mount doesn't force-play again.
      if (autoPlay) {
        const attempt = v.play();
        if (attempt && typeof attempt.then === "function") {
          attempt
            .then(() => { if (typeof onPlayStarted === "function") onPlayStarted(); })
            .catch(() => {
              try { v.muted = true; v.play().then(() => { if (typeof onPlayStarted === "function") onPlayStarted(); }).catch(() => {}); } catch { /* ignore */ }
            });
        }
      }
    };
    v.addEventListener("canplay", onCanPlay, { once: true });

    // Anti-skip: only trigger on INTENTIONAL forward seeks past
    // (maxWatchedPosition + tolerance). Ignore browser-internal seeks,
    // HLS gap-jumps, buffering discontinuities, and our own programmatic
    // corrections.
    const SEEK_TOLERANCE_S = 8;   // generous — HLS + network jitter can be 2-4s
    const onSeeking = () => {
      if (!preventFF) return;
      if (programmaticSeekRef.current) return;   // our seek, or resume — ignore
      // While the initial resume seek is in flight, the browser may fire
      // seek events at 0 and at the resume target back-to-back — none of
      // those are user intent. Silence them until the resume phase ends.
      if (!resumeAppliedRef.current) return;
      const target = v.currentTime;
      const ceiling = (maxWatchedRef.current || 0) + SEEK_TOLERANCE_S;
      // Rewind or in-tolerance seek → allow silently.
      if (target <= ceiling) {
        // Small forward drift inside tolerance still counts as watched.
        if (target > (maxWatchedRef.current || 0)) maxWatchedRef.current = target;
        lastValidPositionRef.current = target;
        return;
      }
      // Genuine forward seek — snap back and warn (throttled to once per 3s).
      startProgrammaticSeek();
      v.currentTime = lastValidPositionRef.current || 0;
      const now = Date.now();
      if (now - lastWarnAtRef.current > 3000) {
        lastWarnAtRef.current = now;
        toast.warning("Fast forward is disabled. Please watch the video completely.");
      }
      reportProgress(v.currentTime, { seekAttempt: true });
    };
    v.addEventListener("seeking", onSeeking);

    // Timeupdate → grow max only on natural forward play; grow unique by delta
    const onTimeUpdate = () => {
      const t = v.currentTime;
      const prev = lastTickRef.current || 0;
      const delta = t - prev;
      if (delta > 0 && delta < 2.5) uniqueRef.current += delta;   // <2.5s ⇒ natural forward tick
      if (t > (maxWatchedRef.current || 0)) maxWatchedRef.current = t;
      // Any natural playback point is a valid rewind target — this ensures
      // the anti-skip snap-back never sends the learner back to 0.
      lastValidPositionRef.current = t;
      if (v.duration) {
        const pct = Math.min(100, (uniqueRef.current / v.duration) * 100);
        watchedPctRef.current = pct;   // ref for reportProgress
        setWatchedPct(pct);            // state for UI (progress bar)
      }
      lastTickRef.current = t;
    };
    v.addEventListener("timeupdate", onTimeUpdate);

    // Playback-rate lockdown
    const onRateChange = () => {
      const r = v.playbackRate;
      if (!speedAllowed) {
        if (r !== 1) { v.playbackRate = 1; toast.info("Playback speed control is disabled."); }
      } else if (!allowedSpeeds.includes(r)) {
        v.playbackRate = 1;
        toast.info(`Only ${allowedSpeeds.join("x, ")}x speeds are allowed.`);
      }
    };
    v.addEventListener("ratechange", onRateChange);
    if (!speedAllowed) v.playbackRate = 1;

    // Kill keyboard-based forward seeks (→, L, J, End, PageDown)
    const onKey = (e) => {
      if (!preventFF) return;
      const blocked = ["ArrowRight", "l", "L", "j", "J", "End", "PageDown"];
      if (blocked.includes(e.key)) {
        e.preventDefault(); e.stopPropagation();
        toast.warning("Fast forward is disabled. Please watch the video completely.");
      }
    };
    v.addEventListener("keydown", onKey);

    // iOS Safari: block the native chromeless fullscreen that strips overlays.
    // We route fullscreen to the container instead (see onDoubleClick).
    const onWebkitFS = async (e) => {
      try { e.preventDefault(); } catch { /* ignore */ }
      const box = containerRef.current;
      if (box && box.requestFullscreen) {
        try { await box.requestFullscreen(); } catch { /* ignore */ }
      } else if (box && box.webkitRequestFullscreen) {
        try { box.webkitRequestFullscreen(); } catch { /* ignore */ }
      }
    };
    v.addEventListener("webkitbeginfullscreen", onWebkitFS);

    // Heartbeat every 8s + on pause / ended
    const timer = setInterval(() => {
      if (v.paused || !v.duration) return;
      // Don't heartbeat until the resume seek has landed — otherwise the
      // very first tick sends currentTime≈0 while maxWatched=<resume>,
      // which the server treats as a normal (valid) point but the *next*
      // tick can look like a fast-forward when the seek finally lands.
      if (!resumeAppliedRef.current) return;
      reportProgress(v.currentTime);
    }, 8000);
    const onPauseOrEnd = () => reportProgress(v.currentTime);
    v.addEventListener("pause", onPauseOrEnd);
    v.addEventListener("ended", onPauseOrEnd);
    // Auto-advance on natural end — mirrors the YouTube STATE_ENDED
    // handler. Fires even when the lesson was already marked completed
    // at mount, so re-watches / restored progress still auto-play the
    // next lesson.
    const onEndedFire = () => fireCompletedOnce();
    v.addEventListener("ended", onEndedFire);

    return () => {
      clearInterval(timer);
      if (programmaticSeekTimerRef.current) {
        clearTimeout(programmaticSeekTimerRef.current);
        programmaticSeekTimerRef.current = null;
      }
      v.removeEventListener("loadedmetadata", onLoaded);
      v.removeEventListener("canplay", onCanPlay);
      v.removeEventListener("seeking", onSeeking);
      v.removeEventListener("timeupdate", onTimeUpdate);
      v.removeEventListener("ratechange", onRateChange);
      v.removeEventListener("keydown", onKey);
      v.removeEventListener("pause", onPauseOrEnd);
      v.removeEventListener("ended", onPauseOrEnd);
      v.removeEventListener("ended", onEndedFire);
      v.removeEventListener("webkitbeginfullscreen", onWebkitFS);
      if (hls) { try { hls.destroy(); } catch { /* ignore */ } }
    };
    // IMPORTANT: `reportProgress` is intentionally EXCLUDED from the dep
    // array. Including it caused the whole HLS attach/destroy pipeline to
    // re-run every ~250 ms during playback (because watchedPct state churn
    // recreated the callback), producing the "buffers repeatedly / auto
    // stop-play" symptom user reports. `reportProgress` reads all mutable
    // state from refs, so keeping a stale closure is safe here.
  }, [payload]);

  if (err) return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
      <ShieldAlert className="mr-2 inline h-4 w-4" /> {err}
    </div>
  );
  if (!payload) return (
    <div className="flex items-center justify-center rounded-lg border border-slate-200 bg-slate-50 p-6">
      <Loader2 className="mr-2 h-4 w-4 animate-spin text-slate-500" />
      <span className="text-xs text-slate-500">Loading video…</span>
    </div>
  );

  // External embed — YouTube now goes through the secure IFrame-API player
  // with anti-skip + progress tracking + Steward watermark. Vimeo / other
  // hosts still fall back to a plain iframe.
  if (payload.source_type === "external_url" && payload.external_url) {
    const url = payload.external_url;
    const isYouTube = url.includes("youtube.com") || url.includes("youtu.be");
    if (isYouTube) {
      return (
        <YouTubeSecurePlayer
          lessonId={lessonId}
          payload={payload}
          onCompleted={onCompleted}
          autoPlay={autoPlay}
          onPlayStarted={onPlayStarted}
          watermarks={
            <>
              <StewardLogoWatermark payload={payload} />
              {payload.security?.dynamic_user_watermark_enabled !== false && (
                <div className="pointer-events-none absolute bottom-20 left-2 z-20 max-w-[70%] select-none sm:bottom-24 sm:left-3" aria-hidden data-testid="yt-watermark-learner">
                  <div className="rounded bg-black/40 px-2 py-1 font-mono text-[9px] leading-tight text-white/80 shadow-sm sm:text-[10px]">
                    <div className="truncate">
                      {(payload.learner?.name || user?.full_name || user?.email || "Learner")}
                      {payload.learner?.email && ` · ${payload.learner.email}`}
                      {payload.learner?.mobile && ` · ${payload.learner.mobile}`}
                    </div>
                    <div className="opacity-70">{tsClock.toLocaleTimeString()}</div>
                  </div>
                </div>
              )}
            </>
          }
        />
      );
    }
    const src = url.includes("vimeo") ? embedVimeo(url) : url;
    return (
      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-black" data-testid="lesson-external-video">
        <div className="aspect-video">
          <iframe src={src} className="h-full w-full" allow="autoplay; encrypted-media"
                  allowFullScreen title={payload.title} />
        </div>
        <StewardLogoWatermark payload={payload} />
      </div>
    );
  }

  const security = payload.security || {};
  const showLearnerWatermark = security.dynamic_user_watermark_enabled !== false;
  const learner = payload.learner || {};
  const learnerLabel = `${learner.name || user?.full_name || user?.email || "Learner"} · ${learner.email || user?.email || ""}${learner.mobile ? " · " + learner.mobile : ""}`;
  const minPct = Number(security.minimum_watch_percentage || 100);
  const durText = payload.duration_seconds ? ` · ${Math.round(payload.duration_seconds / 60)} min` : "";

  return (
    <div
      ref={containerRef}
      className="relative overflow-hidden rounded-2xl border border-slate-200 bg-black"
      data-testid="lesson-secure-video"
    >
      <video
        ref={videoRef}
        controls
        preload="auto"
        playsInline
        // eslint-disable-next-line react/no-unknown-property
        webkit-playsinline="true"
        // eslint-disable-next-line react/no-unknown-property
        x-webkit-airplay="deny"
        controlsList="nodownload noremoteplayback noplaybackrate"
        disablePictureInPicture
        onContextMenu={(e) => { e.preventDefault(); toast.info("Right-click is disabled for this course video."); }}
        onDoubleClick={async (e) => {
          // iOS/Safari kicks the <video> into a chromeless native fullscreen
          // that hides all overlays. Force fullscreen on the container instead
          // so the Steward + learner watermarks stay on-screen.
          e.preventDefault();
          const box = containerRef.current;
          if (!box) return;
          try {
            if (document.fullscreenElement) await document.exitFullscreen();
            else if (box.requestFullscreen) await box.requestFullscreen();
            else if (box.webkitRequestFullscreen) box.webkitRequestFullscreen();
          } catch { /* ignore — permissions */ }
        }}
        className="aspect-video w-full"
        data-testid="lesson-video-element"
        tabIndex={0}
      />

      {/* Steward-logo watermark — top-right, semi-transparent, always visible (survives fullscreen because it's inside the container) */}
      <StewardLogoWatermark payload={payload} />

      {/* Dynamic learner watermark — bottom-left with live timestamp (responsive) */}
      {showLearnerWatermark && (
        <div className="pointer-events-none absolute bottom-14 left-2 max-w-[70%] select-none sm:bottom-16 sm:left-3" aria-hidden data-testid="video-watermark-learner">
          <div className="rounded bg-black/40 px-2 py-1 font-mono text-[9px] leading-tight text-white/80 shadow-sm sm:text-[10px]">
            <div className="truncate">{learnerLabel}</div>
            <div className="opacity-70">{tsClock.toLocaleTimeString()}</div>
          </div>
        </div>
      )}

      {/* Progress overlay (top-left) — smaller on mobile */}
      <div className="pointer-events-none absolute left-2 top-2 rounded bg-black/50 px-2 py-1 text-[9px] font-bold text-white/90 backdrop-blur sm:left-3 sm:top-3 sm:text-[10px]"
           data-testid="video-progress-indicator">
        <span className="text-white/60">Progress: </span>
        {watchedPct.toFixed(0)}%
        <span className="text-white/50"> / {minPct}%{durText}</span>
        {completedRef.current && <span className="ml-2 rounded bg-emerald-500/90 px-1.5 py-0.5 text-[9px] text-white">COMPLETED</span>}
      </div>

      {/* Manual "Mark as complete" — only surfaces when the video-security
          override lowers `manual_complete_percentage` below 100 (rare — the
          course spec now defaults to strict 100 % auto-complete only).
          When effective threshold == 100 the server-side auto-complete
          fires at the same moment, so the manual button would be redundant. */}
      {canMarkComplete && !completedRef.current
        && Number(security.manual_complete_percentage || 100) < 100 && (
        <div className="pointer-events-auto absolute right-2 bottom-14 sm:right-3 sm:bottom-16" data-testid="video-mark-complete-container">
          <button
            type="button"
            onClick={handleMarkComplete}
            disabled={markingComplete}
            data-testid="video-mark-complete-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1.5 text-[11px] font-bold text-white shadow-lg ring-2 ring-white/30 transition hover:bg-emerald-600 disabled:opacity-60"
          >
            {markingComplete ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <CheckCircle2 className="h-3 w-3" />
            )}
            Mark as complete
          </button>
        </div>
      )}
    </div>
  );
}

function StewardLogoWatermark({ payload }) {
  const security = payload?.security || {};
  const branding = payload?.branding || {};
  if (security.steward_logo_watermark_enabled === false) return null;
  const stewardSrc = branding.steward_logo_url || "/steward-logo.png";
  const orgSrc = branding.org_logo_url || "";
  return (
    <div className="pointer-events-none absolute right-2 top-2 flex items-center gap-1.5 select-none sm:right-3 sm:top-3 sm:gap-2" aria-hidden data-testid="video-watermark-brand">
      <div className="flex items-center gap-1 rounded bg-white/85 px-1.5 py-0.5 shadow-sm ring-1 ring-black/5 sm:px-2 sm:py-1">
        <img src={stewardSrc} alt="" className="h-4 w-auto opacity-90 sm:h-5" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        <span className="text-[9px] font-bold uppercase tracking-widest text-[#101876] sm:text-[10px]">Steward</span>
      </div>
      {orgSrc && (
        <img src={orgSrc} alt="" className="h-4 w-auto rounded bg-white/85 p-0.5 opacity-90 shadow-sm ring-1 ring-black/5 sm:h-5"
             onError={(e) => { e.currentTarget.style.display = "none"; }} />
      )}
    </div>
  );
}

/**
 * Helper hook / component: pill shown in course player next to the lesson
 * title telling learners video lessons auto-complete.
 */
export function VideoAutoCompleteBanner({ minWatchPct = 80, watchedPct = 0, completed = false }) {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2 text-[11px]" data-testid="video-autocomplete-banner">
      {completed ? (
        <span className="font-semibold text-emerald-700">✅ Auto-completed. You may proceed to the next lesson.</span>
      ) : (
        <>
          <span className="font-semibold text-amber-800">
            This video lesson will be marked complete automatically after you watch {minWatchPct}% of it.
          </span>
          <span className="ml-2 text-amber-700/80">Current progress: {watchedPct.toFixed(0)}% · target {minWatchPct}%</span>
        </>
      )}
    </div>
  );
}

// Time helper kept in case a caller wants to format resume position
export { fmtTime };
